/**
 * Stage Storage Manager
 *
 * Manages multiple stage data in IndexedDB
 * Each stage has its own storage key based on stageId
 */

import { Stage, Scene } from '../types/stage';
import type { VideoManifest } from '@openmaic/dsl';
import { ChatSession } from '../types/chat';
import { db } from './database';
import { saveChatSessions, loadChatSessions, deleteChatSessions } from './chat-storage';
import { clearPlaybackState } from './playback-storage';
import { clearAllForScene } from '@/lib/quiz/persistence';
import { createLogger } from '@/lib/logger';

const log = createLogger('StageStorage');

export interface StageStoreData {
  stage: Stage;
  scenes: Scene[];
  currentSceneId: string | null;
  chats: ChatSession[];
}

export interface StageListItem {
  id: string;
  name: string;
  description?: string;
  sceneCount: number;
  createdAt: number;
  updatedAt: number;
  interactiveMode?: boolean;
  // ★ 2026-06-08 新增：用于"管理课程"课时下挂"最近学习"课堂
  outlineId?: number | null;
  lessonIndex?: number | null;
  // 进度 0-100（用户学到 N%）
  progress?: number;
  // ★ v0.3.0 升级：舞台的视频清单（manifest 模式）
  videoManifest?: VideoManifest;
}

/**
 * Save stage data to IndexedDB
 */
export async function saveStageData(stageId: string, data: StageStoreData): Promise<void> {
  try {
    const now = Date.now();

    // Save to stages table
    await db.stages.put({
      id: stageId,
      name: data.stage.name || 'Untitled Stage',
      description: data.stage.description,
      createdAt: data.stage.createdAt || now,
      updatedAt: now,
      languageDirective: data.stage.languageDirective,
      style: data.stage.style,
      currentSceneId: data.currentSceneId || undefined,
      agentIds: data.stage.agentIds,
      videoManifest: data.stage.videoManifest,
      interactiveMode: data.stage.interactiveMode,
    });

    // Delete old scenes first to avoid orphaned data
    await db.scenes.where('stageId').equals(stageId).delete();

    // Save new scenes
    if (data.scenes && data.scenes.length > 0) {
      await db.scenes.bulkPut(
        data.scenes.map((scene, index) => ({
          ...scene,
          stageId,
          order: scene.order ?? index,
          createdAt: scene.createdAt || now,
          updatedAt: scene.updatedAt || now,
        })),
      );
    }

    // Save chat sessions to independent table
    if (data.chats) {
      await saveChatSessions(stageId, data.chats);
    }

    log.info(`Saved stage: ${stageId}`);
  } catch (error) {
    log.error('Failed to save stage:', error);
    throw error;
  }
}

/**
 * ★ 第一波·服务端兜底：只保存课堂元数据（name/description/sceneCount 等）到 IndexedDB
 *   场景：首页"最近学习"从服务端拉回云端课堂时调用
 *   区别于 saveStageData：本函数不写 scenes（云端恢复时没有 scenes 数据）
 *   后续用户点击进入 classroom 页时，classroom 页会从 /api/classroom?id=xxx 拉全量数据补齐 scenes
 * 2026-06-08 升级：支持 outlineId + lessonIndex + progress（用于"管理课程"课时下挂"最近学习"）
 */
export async function saveStageMetaOnly(meta: StageListItem): Promise<void> {
  try {
    const now = Date.now();
    // ★ 注意：db.stages.put() 是完整替换，所以必须把所有字段都带上
    await db.stages.put({
      id: meta.id,
      name: meta.name || 'Untitled Stage',
      description: meta.description,
      createdAt: meta.createdAt || now,
      updatedAt: meta.updatedAt || now,
      interactiveMode: meta.interactiveMode,
      outlineId: meta.outlineId ?? undefined,
      lessonIndex: meta.lessonIndex ?? undefined,
      progress: meta.progress ?? 0,
    });
    // 【v6.48.2 降噪 2026-07-11】log.info → log.debug
    //   老板反馈：100+ 个课堂恢复时 console spam（每个课堂 1 条 log）
    //   修复：log.debug，devTools 默认隐藏（debug level 0 < info level 1）
    //   兜底总结 log 仍保留在 page.tsx:480 ('IndexedDB empty, recovered N classrooms from server')
    //   仅本函数详情降噪，v6.46 修复的兜底能力（换浏览器/清缓存恢复 100 个历史课堂）仍可见
    log.debug(`Saved stage meta only: ${meta.id} (outlineId=${meta.outlineId}, lessonIndex=${meta.lessonIndex}, progress=${meta.progress})`);
  } catch (error) {
    log.error('Failed to save stage meta only:', error);
    throw error;
  }
}

/**
 * ★ 第一波·服务端兜底（批量版本）：一次 bulkPut 写多个课堂元数据
 *   场景：首页"最近学习"从服务端拉回 100+ 个云端课堂时调用
 *   修法：使用 Dexie 的 bulkPut() 一次写入多条记录
 *   收益：100 次 put (串行 await) → 1 次 bulkPut
 *        - 100 条 console.log → 1 条 log.debug
 *        - IndexedDB 卡 200-500ms → <50ms
 *   修因：v6.46 9ac05689 引入的 saveStageMetaOnly 串行循环让首页首次加载卡 500ms
 *        老板 2026-07-11 反馈"打开慢"，byte-diff 后定位到此
 * 2026-07-11 v6.48.2 batch 优化
 */
export async function saveStageMetaOnlyBatch(metaList: StageListItem[]): Promise<void> {
  try {
    if (metaList.length === 0) return;
    const now = Date.now();
    const records = metaList.map((meta) => ({
      id: meta.id,
      name: meta.name || 'Untitled Stage',
      description: meta.description,
      createdAt: meta.createdAt || now,
      updatedAt: meta.updatedAt || now,
      interactiveMode: meta.interactiveMode,
      outlineId: meta.outlineId ?? undefined,
      lessonIndex: meta.lessonIndex ?? undefined,
      progress: meta.progress ?? 0,
    }));
    // ★ 关键：Dexie bulkPut() 一次事务写入所有记录（vs 100 次 put 串行）
    await db.stages.bulkPut(records);
    log.debug(`[loadClassrooms] bulkPut ${metaList.length} stage meta (batch save, 1 transaction)`);
  } catch (error) {
    log.error('Failed to bulk save stage meta only:', error);
    throw error;
  }
}

/**
 * Load stage data from IndexedDB
 */
export async function loadStageData(stageId: string): Promise<StageStoreData | null> {
  try {
    // Load stage
    const stage = await db.stages.get(stageId);
    if (!stage) {
      log.info(`Stage not found: ${stageId}`);
      return null;
    }

    // Load scenes
    const scenes = await db.scenes.where('stageId').equals(stageId).sortBy('order');

    // Load chat sessions from independent table
    const chats = await loadChatSessions(stageId);

    log.info(`Loaded stage: ${stageId}, scenes: ${scenes.length}, chats: ${chats.length}`);

    return {
      stage,
      scenes,
      currentSceneId: stage.currentSceneId || scenes[0]?.id || null,
      chats,
    };
  } catch (error) {
    log.error('Failed to load stage:', error);
    return null;
  }
}

/**
 * Delete stage and all related data
 */
export async function deleteStageData(stageId: string): Promise<void> {
  try {
    // Collect scene ids before deletion so we can sweep per-scene localStorage
    // keys (quiz draft / submitted answers / graded results).
    const sceneIds = (await db.scenes.where('stageId').equals(stageId).toArray()).map((s) => s.id);

    // Delete stage
    await db.stages.delete(stageId);

    // Delete scenes
    await db.scenes.where('stageId').equals(stageId).delete();

    // Delete chat sessions and playback state
    await deleteChatSessions(stageId);
    await clearPlaybackState(stageId);

    // Sweep quiz persistence keys for each deleted scene.
    for (const sceneId of sceneIds) {
      clearAllForScene(sceneId);
    }

    log.info(`Deleted stage: ${stageId}`);
  } catch (error) {
    log.error('Failed to delete stage:', error);
    throw error;
  }
}

/**
 * List all stages
 */
export async function listStages(): Promise<StageListItem[]> {
  try {
    const stages = await db.stages.orderBy('updatedAt').reverse().toArray();

    const stageList: StageListItem[] = await Promise.all(
      stages.map(async (stage) => {
        const sceneCount = await db.scenes.where('stageId').equals(stage.id).count();

        return {
          id: stage.id,
          name: stage.name,
          description: stage.description,
          sceneCount,
          createdAt: stage.createdAt,
          updatedAt: stage.updatedAt,
          interactiveMode: stage.interactiveMode,
          // ★ 2026-06-08 透传 outlineId/lessonIndex/progress
          outlineId: stage.outlineId ?? null,
          lessonIndex: stage.lessonIndex ?? null,
          progress: stage.progress ?? 0,
        };
      }),
    );

    return stageList;
  } catch (error) {
    log.error('Failed to list stages:', error);
    return [];
  }
}

type ThumbnailMediaElement = {
  type: string;
  src?: string;
  mediaRef?: string;
  poster?: string;
};

type ThumbnailSlide = import('../types/slides').Slide;

function isGeneratedMediaRef(value: unknown): value is string {
  return typeof value === 'string' && /^gen_(img|vid)_[\w-]+$/i.test(value);
}

function isLegacySequentialVideoRef(value: unknown): value is string {
  return typeof value === 'string' && /^gen_vid_\d+$/i.test(value);
}

function getThumbnailMediaRef(element: ThumbnailMediaElement): string | undefined {
  if (element.type === 'image' && isGeneratedMediaRef(element.src)) {
    return element.src;
  }
  if (element.type === 'video') {
    if (isGeneratedMediaRef(element.mediaRef)) return element.mediaRef;
    if (isGeneratedMediaRef(element.src)) return element.src;
  }
  return undefined;
}

function getMediaRecordElementId(recordId: string): string {
  return recordId.includes(':') ? recordId.split(':').slice(1).join(':') : recordId;
}

function blobWithType(blob: Blob, mimeType: string): Blob {
  return blob.type ? blob : new Blob([blob], { type: mimeType });
}

function revokeObjectUrl(url: string | undefined) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

export function revokeThumbnailSlideMediaUrls(slides: Record<string, ThumbnailSlide>) {
  for (const slide of Object.values(slides)) {
    for (const element of slide.elements as ThumbnailMediaElement[]) {
      if (element.type === 'image' || element.type === 'video') {
        revokeObjectUrl(element.src);
      }
      if (element.type === 'video') {
        revokeObjectUrl(element.poster);
      }
    }
  }
}

/**
 * Get first slide scene's canvas data for each stage (for thumbnail preview).
 * Also resolves generated image/video refs from mediaFiles so thumbnails show real media.
 * Returns a map of stageId -> Slide (canvas data with resolved media)
 */
export async function getFirstSlideByStages(
  stageIds: string[],
): Promise<Record<string, ThumbnailSlide>> {
  const result: Record<string, ThumbnailSlide> = {};
  try {
    await Promise.all(
      stageIds.map(async (stageId) => {
        const scenes = await db.scenes.where('stageId').equals(stageId).sortBy('order');
        const firstSlide = scenes.find((s) => s.content?.type === 'slide');
        if (firstSlide && firstSlide.content.type === 'slide') {
          const slide = structuredClone(firstSlide.content.canvas);

          const mediaElements = slide.elements.filter((el) =>
            getThumbnailMediaRef(el as ThumbnailMediaElement),
          );
          if (mediaElements.length > 0) {
            const mediaRecords = await db.mediaFiles.where('stageId').equals(stageId).toArray();
            const videoRecords = mediaRecords.filter(
              (record) => !record.error && record.type === 'video',
            );
            const mediaMap = new Map(
              mediaRecords.map((record) => [getMediaRecordElementId(record.id), record] as const),
            );

            for (const el of mediaElements as ThumbnailMediaElement[]) {
              const mediaRef = getThumbnailMediaRef(el);
              const exactRecord = mediaRef ? mediaMap.get(mediaRef) : undefined;
              const usableExactRecord = exactRecord && !exactRecord.error ? exactRecord : undefined;
              const legacyRecord =
                !exactRecord &&
                el.type === 'video' &&
                isLegacySequentialVideoRef(mediaRef) &&
                videoRecords.length === 1
                  ? videoRecords[0]
                  : undefined;
              const record = usableExactRecord ?? legacyRecord;

              if (!mediaRef || !record) {
                if (el.type === 'image') {
                  // Clear unresolved placeholder so BaseImageElement won't subscribe
                  // to the global media store (which may have stale data from another course)
                  el.src = '';
                }
                continue;
              }

              if (el.type === 'image' && record.type === 'image') {
                el.src = URL.createObjectURL(blobWithType(record.blob, record.mimeType));
              } else if (el.type === 'video' && record.type === 'video') {
                el.src = URL.createObjectURL(blobWithType(record.blob, record.mimeType));
                if (record.poster) {
                  el.poster = URL.createObjectURL(blobWithType(record.poster, 'image/jpeg'));
                }
              } else if (el.type === 'image') {
                el.src = '';
              }
            }
          }

          result[stageId] = slide;
        }
      }),
    );
  } catch (error) {
    log.error('Failed to load thumbnails:', error);
  }
  return result;
}

/**
 * Rename a stage (updates only the name field in IndexedDB)
 */
export async function renameStage(stageId: string, newName: string): Promise<void> {
  try {
    await db.stages.update(stageId, { name: newName, updatedAt: Date.now() });
    log.info(`Renamed stage ${stageId} to "${newName}"`);
  } catch (error) {
    log.error('Failed to rename stage:', error);
    throw error;
  }
}

/**
 * Check if stage exists
 */
export async function stageExists(stageId: string): Promise<boolean> {
  try {
    const stage = await db.stages.get(stageId);
    return !!stage;
  } catch (error) {
    log.error('Failed to check stage existence:', error);
    return false;
  }
}
